import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign } from 'lucide-react';

/**
 * Commission Analytics Chart Component
 * Displays referrer commission trends over time
 *
 * @param {Array} data - Array of commission data objects with date, earned, and paid
 * @param {string} chartType - 'line' or 'area' chart type
 */
const CommissionAnalyticsChart = ({ data = [], chartType = 'area' }) => {
    const formatCurrency = (value) => {
        return `₦${value.toLocaleString()}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            month: 'short',
            year: 'numeric'
        });
    };

    // If no data, show empty state
    if (!data || data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-6 h-6 text-accent-gold" />
                    <h3 className="text-lg font-bold text-primary-black">Commission Trend</h3>
                </div>
                <div className="h-64 flex items-center justify-center text-gray-500">
                    <p>No commission data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-accent-gold" />
                    <h3 className="text-lg font-bold text-primary-black">Commission Trend</h3>
                </div>
                <div className="text-sm text-gray-600">
                    Last {data.length} {data.length === 1 ? 'period' : 'periods'}
                </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
                {chartType === 'line' ? (
                    <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <YAxis
                            tickFormatter={formatCurrency}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <Tooltip
                            formatter={(value) => formatCurrency(value)}
                            labelFormatter={formatDate}
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px'
                            }}
                        />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="earned"
                            stroke="#D4AF37"
                            strokeWidth={2}
                            name="Earned"
                            dot={{ fill: '#D4AF37', r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="paid"
                            stroke="#10B981"
                            strokeWidth={2}
                            name="Paid"
                            dot={{ fill: '#10B981', r: 4 }}
                        />
                    </LineChart>
                ) : (
                    <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                            <linearGradient id="colorEarned" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.1} />
                            </linearGradient>
                            <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <YAxis
                            tickFormatter={formatCurrency}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <Tooltip
                            formatter={(value) => formatCurrency(value)}
                            labelFormatter={formatDate}
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px'
                            }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="earned"
                            stroke="#D4AF37"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorEarned)"
                            name="Earned"
                        />
                        <Area
                            type="monotone"
                            dataKey="paid"
                            stroke="#10B981"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorPaid)"
                            name="Paid"
                        />
                    </AreaChart>
                )}
            </ResponsiveContainer>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                <div className="text-center">
                    <p className="text-sm text-gray-500">Total Earned</p>
                    <p className="text-lg font-bold text-accent-gold">
                        {formatCurrency(data.reduce((sum, item) => sum + item.earned, 0))}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Total Paid</p>
                    <p className="text-lg font-bold text-green-600">
                        {formatCurrency(data.reduce((sum, item) => sum + item.paid, 0))}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Outstanding</p>
                    <p className="text-lg font-bold text-red-600">
                        {formatCurrency(
                            data.reduce((sum, item) => sum + item.earned, 0) -
                            data.reduce((sum, item) => sum + item.paid, 0)
                        )}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Avg/Period</p>
                    <p className="text-lg font-bold text-blue-600">
                        {formatCurrency(data.reduce((sum, item) => sum + item.earned, 0) / data.length)}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CommissionAnalyticsChart;
